/** Drain the offline queue: upload photos + voice → post inspection → GC.
 * Each pending record is retried with exponential backoff (see markPendingFailed).
 * A best-effort heartbeat is emitted to the server after each pass so the
 * Supervisor's Device Sync page reflects reality.
 */
import {
  listPending, listQueuedPhotos, removePhoto, getQueuedAudio, removeAudio,
  removePending, markPendingFailed, setMeta,
} from "./db";
import { isOnline } from "./network";

export async function syncPending(api, onProgress) {
  if (!isOnline()) return { synced: 0, skipped: 0, total: 0 };
  const pending = await listPending();
  let synced = 0;
  let skipped = 0;
  const total = pending.length;

  for (const p of pending) {
    // Backoff gate — skip anything that hasn't reached its next-attempt time yet
    if (p.next_attempt_at && new Date(p.next_attempt_at) > new Date()) {
      skipped += 1;
      continue;
    }
    try {
      onProgress?.({ id: p.id, status: "uploading" });
      const uploadedPhotos = await _uploadQueuedPhotos(api, p.local_photo_ids || []);
      const payload = {
        ...p.payload,
        photos: uploadedPhotos.length ? uploadedPhotos : p.payload.photos,
      };
      // Preserve original capture date as the inspection date
      if (p.original_date) payload.date = p.original_date;
      await _attachQueuedAudioIfAny(api, p, payload);
      await api.post("/inspections", payload);
      await removePending(p.id);
      synced += 1;
      onProgress?.({ id: p.id, status: "synced" });
    } catch (err) {
      console.warn("sync failed", err);
      await markPendingFailed(p.id, err?.message || String(err));
      skipped += 1;
      onProgress?.({ id: p.id, status: "failed" });
    }
  }

  await _emitHeartbeat(api);
  return { synced, skipped, total };
}

async function _uploadQueuedPhotos(api, ids) {
  const records = await listQueuedPhotos(ids);
  const uploaded = [];
  for (const ph of records) {
    const fd = new FormData();
    fd.append("file", ph.blob, `photo_${ph.id}.jpg`);
    fd.append("gps_lat", String(ph.gps_lat));
    fd.append("gps_lng", String(ph.gps_lng));
    fd.append("taken_at", ph.taken_at);  // original capture time preserved
    const res = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    uploaded.push({
      file_id: res.data.file_id,
      gps_lat: res.data.gps_lat,
      gps_lng: res.data.gps_lng,
      taken_at: res.data.taken_at,
    });
    await removePhoto(ph.id);
  }
  return uploaded;
}

async function _attachQueuedAudioIfAny(api, pendingRec, payload) {
  if (!pendingRec.local_audio_id) return;
  const audio = await getQueuedAudio(pendingRec.local_audio_id);
  if (!audio) return;
  const fd = new FormData();
  const ext = (audio.mime || "audio/webm").includes("webm") ? "webm" : "mp4";
  fd.append("file", audio.blob, `note_${pendingRec.local_audio_id}.${ext}`);
  fd.append("taken_at", audio.taken_at || pendingRec.original_date || new Date().toISOString());
  const ares = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  payload.voice_note_file_id = ares.data.file_id;
  await removeAudio(pendingRec.local_audio_id);
}

async function _emitHeartbeat(api) {
  const now = new Date().toISOString();
  await setMeta("last_synced_at", now);
  const remaining = await listPending();
  const oldest = remaining.length
    ? remaining.reduce((a, b) => (a.queued_at < b.queued_at ? a : b)).queued_at
    : null;
  try {
    await api.post("/sync-status/heartbeat", {
      pending_count: remaining.length,
      oldest_pending_at: oldest,
      last_synced_at: now,
    });
  } catch (e) {
    console.debug("[offline] heartbeat failed (best-effort):", e?.message);
  }
}
